/**
 * FX exposure and period-end revaluation (FSD Part 2, FIN-FR-023).
 *
 * The wiring packet spreads `FX_EXPOSURE_ROUTES` into the router and adds the
 * nav item described by `FX_EXPOSURE_NAV_ITEM` to section s-2.1. The run detail
 * is reached only from the runs tab, so it needs a nav alias onto
 * `fx-exposure` (nav/aliases.ts), not a nav item of its own.
 *
 * Both routes are gated on `fx.exposure.read`, which is what every GET of
 * `internal/fxreval` declares. Preparing, posting, discarding and configuring
 * are checked on the action.
 */

import type { RouteRecordRaw } from 'vue-router'

import { FX_EXPOSURE_PERMISSIONS } from '@/modules/fx/exposure/permissions'

export const FX_EXPOSURE_PATHS = {
  root: '/finance/fx-exposure',
  run: (id: number | string): string => `/finance/fx-exposure/runs/${id}`,
  journal: (id: number | string): string => `/gl/journal-entries/${id}`,
} as const

export const FX_EXPOSURE_TABS = ['exposure', 'runs', 'settings'] as const

export type FxExposureTab = (typeof FX_EXPOSURE_TABS)[number]

export const FX_EXPOSURE_ROUTES: RouteRecordRaw[] = [
  {
    path: '/finance/fx-exposure',
    name: 'fx-exposure',
    component: () => import('@/modules/fx/exposure/FxExposureView.vue'),
    meta: { titleKey: 'fx.exposure.routeTitle', permission: FX_EXPOSURE_PERMISSIONS.exposureRead },
  },
  {
    path: '/finance/fx-exposure/runs/:id(\\d+)',
    name: 'fx-exposure-run',
    component: () => import('@/modules/fx/exposure/RevaluationRunView.vue'),
    meta: { titleKey: 'fx.exposure.run.routeTitle', permission: FX_EXPOSURE_PERMISSIONS.exposureRead },
  },
]

/** The nav item INT adds to p02 section s-2.1 (shape of nav/parts items). */
export const FX_EXPOSURE_NAV_ITEM = {
  id: 'fx-exposure',
  labelKey: 'fx.exposure.navLabel',
  to: FX_EXPOSURE_PATHS.root,
  icon: 'chart',
  permission: FX_EXPOSURE_PERMISSIONS.exposureRead,
  code: 'FIN-FR-023',
  descriptionKey: 'fx.exposure.navDescription',
} as const
